import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { api, type DashboardItem } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { StandCell } from '../components/StandCell';
import { LiveFeedWidget, AktiveArbeiterWidget, AbteilungHeatmapWidget, QSWidget } from '../components/LiveWidgets';
import { HeuteGesamtWidget } from '../components/HeuteWidget';
import DurchlaufzeitWidget from '../components/DurchlaufzeitWidget';
import KapazitaetWidget from '../components/KapazitaetWidget';
import WareneingangWidget from '../components/WareneingangWidget';
import SachbearbeiterWorkloadWidget from '../components/SachbearbeiterWorkloadWidget';
import TageszielWidget from '../components/TageszielWidget';

export function Dashboard() {
  const dashboard = useApi(() => api.dashboard.me(), []);
  const aufgaben  = useApi(() => api.dashboard.meineAufgaben(), []);
  const standWerte = useApi(() => api.lookup.standWerte(), []);
  // Consolidated live — 1 endpoint ersetzt 5 parallele Widget-Calls
  const live = useApi(() => api.dashboard.live(), []);
  useAutoRefresh(() => live.reload());

  return (
    <div className="p-8">
      <header className="mb-6 flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Start</div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        </div>
        <NeuerAuftragMenu />
      </header>

      {dashboard.loading && <div className="text-slate-500">Lädt …</div>}
      {dashboard.error   && <div className="text-red-600">Fehler: {dashboard.error.message}</div>}

      {dashboard.data && (
        <>
          {/* Neu hinzugefügte Aufträge — ganz oben, damit sofort sichtbar */}
          {dashboard.data.neu.length > 0 && (
            <NeuSection items={dashboard.data.neu} />
          )}

          {/* Überblick */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Offene gesamt"
              value={dashboard.data.offenGesamt}
              tone={dashboard.data.offenUeberfaellig > 0 ? 'red' : 'default'}
              subtitle={
                dashboard.data.offenUeberfaellig > 0
                  ? `${dashboard.data.offenUeberfaellig} überfällig`
                  : 'alle Module · alle Sachbearbeiter'
              }
            />
            <StatCard label="Diese Woche"
                      value={dashboard.data.aktuelleWoche.offen + dashboard.data.aktuelleWoche.fertig}
                      subtitle={`${dashboard.data.aktuelleWoche.fertig} fertig · ${dashboard.data.aktuelleWoche.offen} offen`} />
            <StatCard label="Eilige Aufträge"
                      value={dashboard.data.eilig.total}
                      tone={dashboard.data.eilig.ueberfaellig > 0 ? 'red' : 'default'}
                      subtitle={dashboard.data.eilig.ueberfaellig > 0 ? `${dashboard.data.eilig.ueberfaellig} überfällig` : undefined} />
            <StatCard label="Reststunden bis Freitag"
                      value={`${Math.round(dashboard.data.aktuelleWoche.reststundenBisFreitag)} Std`}
                      tone={dashboard.data.aktuelleWoche.ueberfaelligeStunden > 0 ? 'red' : 'default'}
                      subtitle={
                        dashboard.data.aktuelleWoche.ueberfaelligeStunden > 0
                          ? `${Math.round(dashboard.data.aktuelleWoche.ueberfaelligeStunden)} Std überfällig`
                          : 'alle Module · offen'
                      } />
          </div>

          {/* Heute Soll/Ist/Rest Gesamt — was muss HEUTE geliefert werden */}
          <HeuteGesamtWidget />

          {/* Tagesziel — heutige Produktion pro Abteilung */}
          <TageszielWidget />

          {/* Termin-Dringlichkeit — Ampel (anklickbar) — Liefertermin-Sicht */}
          <section className="mb-6">
            <h2 className="font-semibold text-slate-900 mb-3">
              Termin-Übersicht <span className="text-sm text-slate-500 font-normal">(offene Aufträge · klicken zum Filtern)</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <UrgencyCard tone="red"     bucket="ueberfaellig"
                           label="Überfällig"    hint="Liefertermin bereits vorbei"
                           count={dashboard.data.termine.counts.ueberfaellig} />
              <UrgencyCard tone="orange"  bucket="kritisch"
                           label="Kritisch"      hint="heute bis in 3 Tagen"
                           count={dashboard.data.termine.counts.kritisch} />
              <UrgencyCard tone="yellow"  bucket="bald"
                           label="Bald"          hint="in 4–7 Tagen"
                           count={dashboard.data.termine.counts.bald} />
              <UrgencyCard tone="emerald" bucket="naechste_woche"
                           label="Nächste Woche" hint="in 8–14 Tagen"
                           count={dashboard.data.termine.counts.naechste_woche} />
            </div>
          </section>

          {/* Überfällige Liste — sofort sichtbar */}
          {dashboard.data.termine.ueberfaellig.length > 0 && (
            <UrgencySection title="⚠ Überfällig — sofort prüfen"
                           accent="red"
                           items={dashboard.data.termine.ueberfaellig}
                           standWerte={standWerte.data?.data ?? []}
                           onReload={() => dashboard.reload()} />
          )}
          {dashboard.data.termine.kritisch.length > 0 && (
            <UrgencySection title="Kritisch — in den nächsten 3 Tagen"
                           accent="orange"
                           items={dashboard.data.termine.kritisch}
                           standWerte={standWerte.data?.data ?? []}
                           onReload={() => dashboard.reload()} />
          )}
          {dashboard.data.termine.bald.length > 0 && (
            <UrgencySection title="Bald — in 4–7 Tagen"
                           accent="yellow"
                           items={dashboard.data.termine.bald}
                           standWerte={standWerte.data?.data ?? []}
                           onReload={() => dashboard.reload()} />
          )}

          {/* Abteilung-Heatmap — operative Übersicht */}
          <div className="mb-6">
            <AbteilungHeatmapWidget
              externalData={live.data?.heatmap}
              externalError={live.error}
              externalReload={live.reload}
            />
          </div>

          {/* QS Widget */}
          <div className="mb-6">
            <QSWidget
              externalData={live.data?.qs}
              externalError={live.error}
              externalReload={live.reload}
            />
          </div>

          {/* Live Feed + Aktive Arbeiter */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <LiveFeedWidget
              externalData={live.data?.feed}
              externalError={live.error}
              externalReload={live.reload}
            />
            <AktiveArbeiterWidget
              externalData={live.data?.active}
              externalError={live.error}
              externalReload={live.reload}
            />
          </div>

          {/* Analytik: Durchlaufzeit + Kapazität + Wareneingang */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <DurchlaufzeitWidget />
            <KapazitaetWidget />
            <WareneingangWidget />
          </div>

          {/* Sachbearbeiter-Workload — Management-Sicht (nach unten) */}
          <SachbearbeiterWorkloadWidget data={dashboard.data.sachbearbeiterWorkload} />
        </>
      )}

      {/* Meine Aufgaben */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mt-8">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Meine Aufgaben</h2>
          <Link to="/drueckteile" className="text-xs text-brand hover:underline">Alle anzeigen →</Link>
        </div>

        {aufgaben.loading && <div className="p-5 text-slate-500">Lädt …</div>}
        {aufgaben.data && aufgaben.data.data.length === 0 && (
          <div className="p-8 text-center text-slate-500">Keine offenen Aufgaben</div>
        )}
        {aufgaben.data && aufgaben.data.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
              <tr>
                <th className="text-left px-5 py-2 font-medium">DV-Nr</th>
                <th className="text-left px-5 py-2 font-medium">Kunde</th>
                <th className="text-left px-5 py-2 font-medium">Bezeichnung</th>
                <th className="text-left px-5 py-2 font-medium">Stand</th>
                <th className="text-right px-5 py-2 font-medium">Zeit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {aufgaben.data.data.map(d => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <Link to={`/drueckteile/${d.id}`} className="font-mono text-xs text-brand hover:underline">
                      {d.dv_nummer ?? '—'}
                    </Link>
                  </td>
                  <td className="px-5 py-3">{d.kunde_name ?? '—'}</td>
                  <td className="px-5 py-3">
                    {d.masse_bezeichnung ?? '—'}
                    {d.priority === 'eilig'    && <span className="ml-2 text-xs font-bold text-red-600">EILIG</span>}
                    {d.priority === 'kritisch' && <span className="ml-2 text-xs font-bold text-red-800">KRITISCH</span>}
                  </td>
                  <td className="px-5 py-3">
                    {d.stand_wert && <StatusBadge label={d.stand_wert} color={d.stand_farbe} />}
                  </td>
                  <td className="px-5 py-3 text-right">{d.gesamtzeit}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const ABTEILUNGEN: { modul: string; label: string }[] = [
  { modul: 'drueckteile', label: 'Drückteile' },
  { modul: 'laufraeder',  label: 'Laufräder' },
  { modul: 'baugruppen',  label: 'Baugruppen' },
  { modul: 'montage',     label: 'Montage' },
  { modul: 'laser_kant',  label: 'Laser/Kant' },
];

function NeuerAuftragMenu() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} className="btn-primary">+ Neuer Auftrag</button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-slate-200 shadow-lg z-20 overflow-hidden">
            <div className="px-4 py-2 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
              Für welche Abteilung?
            </div>
            {ABTEILUNGEN.map(a => (
              <Link key={a.modul} to={`/${a.modul}/neu`}
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                {a.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function relativeZeit(iso?: string): string {
  if (!iso) return '';
  // created_at wird als UTC ohne Zeitzone gespeichert → 'Z' anhängen
  const then = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z')).getTime();
  const diffMin = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (diffMin < 1)   return 'gerade eben';
  if (diffMin < 60)  return `vor ${diffMin} Min`;
  const diffStd = Math.round(diffMin / 60);
  if (diffStd < 24)  return `vor ${diffStd} Std`;
  const diffTage = Math.round(diffStd / 24);
  return diffTage === 1 ? 'vor 1 Tag' : `vor ${diffTage} Tagen`;
}

const MODUL_DEFAULT = { label: 'Drückteile', cls: 'bg-sky-100 text-sky-800' };
const MODUL_INFO: Record<string, { label: string; cls: string }> = {
  drueckteile: MODUL_DEFAULT,
  laufraeder:  { label: 'Laufräder',  cls: 'bg-violet-100 text-violet-800' },
  baugruppen:  { label: 'Baugruppen', cls: 'bg-emerald-100 text-emerald-800' },
  montage:     { label: 'Montage',    cls: 'bg-amber-100 text-amber-800' },
  laser_kant:  { label: 'Laser/Kant', cls: 'bg-rose-100 text-rose-800' },
};

function ModulBadge({ modul }: { modul?: string }) {
  const info = MODUL_INFO[modul ?? 'drueckteile'] ?? MODUL_DEFAULT;
  return <span className={`${info.cls} px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap`}>{info.label}</span>;
}

function NeuSection({ items }: { items: DashboardItem[] }) {
  return (
    <section className="mb-6">
      <div className="bg-sky-50 border border-sky-200 rounded-xl overflow-hidden overflow-x-auto">
        <div className="px-5 py-3 border-b border-sky-200 flex items-center justify-between bg-white/50">
          <h2 className="font-semibold text-sky-800 flex items-center gap-2">
            <span>🆕</span> Neu hinzugefügt
            <span className="text-sm text-slate-500 font-normal">(in den letzten 7 Tagen angelegt)</span>
          </h2>
          <span className="text-xs text-slate-600">{items.length} Aufträge</span>
        </div>
        <table className="w-full text-[13px] bg-white min-w-[1080px]">
          <thead className="text-xs text-slate-600 uppercase bg-slate-50">
            <tr>
              <th className="text-left px-2.5 py-1.5 font-medium">Typ</th>
              <th className="text-left px-2.5 py-1.5 font-medium">Bestellnr</th>
              <th className="text-left px-2.5 py-1.5 font-medium">DV-Nr</th>
              <th className="text-left px-2.5 py-1.5 font-medium">Pos</th>
              <th className="text-left px-2.5 py-1.5 font-medium">Kunde</th>
              <th className="text-left px-2.5 py-1.5 font-medium">Bezeichnung</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Stk</th>
              <th className="text-left px-2.5 py-1.5 font-medium">Liefertermin</th>
              <th className="text-center px-2.5 py-1.5 font-medium">KW</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Zeit</th>
              <th className="text-left px-2.5 py-1.5 font-medium">Stand</th>
              <th className="text-left px-2.5 py-1.5 font-medium">SB</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Angelegt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map(d => (
              <tr key={`${d.modul ?? 'drueckteile'}-${d.id}`} className="hover:bg-slate-50">
                <td className="px-2.5 py-1.5"><ModulBadge modul={d.modul} /></td>
                <td className="px-2.5 py-1.5 font-mono text-xs">
                  <div className="flex items-center gap-1">
                    <span className="bg-sky-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">Neu</span>
                    {d.bestellnr}
                  </div>
                </td>
                <td className="px-2.5 py-1.5 font-mono text-xs">
                  <Link to={`/${d.modul ?? 'drueckteile'}/${d.id}`} className="text-brand hover:underline">{d.dv_nummer ?? '—'}</Link>
                </td>
                <td className="px-2.5 py-1.5">{d.position ?? '—'}</td>
                <td className="px-2.5 py-1.5 max-w-[150px] truncate" title={d.kunde_name ?? ''}>{d.kunde_name ?? '—'}</td>
                <td className="px-2.5 py-1.5 max-w-[190px] truncate" title={d.masse_bezeichnung ?? ''}>{d.masse_bezeichnung ?? '—'}</td>
                <td className="px-2.5 py-1.5 text-right">{d.stueck}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{d.lieferdatum ? <NeuLieferDatum iso={d.lieferdatum} days={d.days_to_delivery} /> : '—'}</td>
                <td className="px-2.5 py-1.5 text-center">
                  <span className="bg-sky-100 text-sky-900 px-2 py-0.5 rounded text-xs font-semibold">{d.kw}</span>
                </td>
                <td className="px-2.5 py-1.5 text-right whitespace-nowrap">{d.gesamtzeit}h</td>
                <td className="px-2.5 py-1.5">
                  {d.stand_wert ? <StatusBadge label={d.stand_wert} color={d.stand_farbe ?? undefined} /> : '—'}
                </td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">
                  <span className="bg-slate-100 rounded px-2 py-0.5 text-xs">{d.sachbearbeiter_name ?? '—'}</span>
                </td>
                <td className="px-2.5 py-1.5 text-right text-slate-500 whitespace-nowrap">{relativeZeit(d.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function formatDatum(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function NeuLieferDatum({ iso, days }: { iso: string; days: number }) {
  const formatted = formatDatum(iso);
  let cls = 'text-slate-700';
  let badge: string | null = null;
  if (days < 0)       { cls = 'text-red-700 font-semibold';    badge = `${Math.abs(days)}T verspätet`; }
  else if (days === 0){ cls = 'text-red-700 font-semibold';    badge = 'HEUTE'; }
  else if (days <= 3) { cls = 'text-orange-700 font-semibold'; badge = days === 1 ? 'morgen' : `in ${days}T`; }
  else if (days <= 7) { cls = 'text-amber-700'; }
  return (
    <span className={cls}>{formatted}{badge && <span className="ml-1 text-[10px] opacity-75">· {badge}</span>}</span>
  );
}

function StatCard({ label, value, subtitle, tone = 'default' }: {
  label: string; value: number | string; subtitle?: string; tone?: 'default' | 'red';
}) {
  const valueColor = tone === 'red' ? 'text-red-600' : 'text-slate-900';
  return (
    <div className="bg-white rounded-xl p-5 border border-slate-200">
      <div className="text-xs text-slate-500 uppercase">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${valueColor}`}>{value}</div>
      {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
    </div>
  );
}

const urgencyStyles = {
  red:     { bg: 'bg-red-50',    border: 'border-red-200',    value: 'text-red-700',    dot: 'bg-red-500' },
  orange:  { bg: 'bg-orange-50', border: 'border-orange-200', value: 'text-orange-700', dot: 'bg-orange-500' },
  yellow:  { bg: 'bg-amber-50',  border: 'border-amber-200',  value: 'text-amber-700',  dot: 'bg-amber-500' },
  emerald: { bg: 'bg-emerald-50',border: 'border-emerald-200',value: 'text-emerald-700',dot: 'bg-emerald-500' },
} as const;

function UrgencyCard({ tone, label, hint, count, bucket }: {
  tone: keyof typeof urgencyStyles; label: string; hint: string; count: number;
  bucket: 'ueberfaellig' | 'kritisch' | 'bald' | 'naechste_woche';
}) {
  const s = urgencyStyles[tone];
  return (
    <Link to={`/drueckteile?bucket=${bucket}`}
          className={`block ${s.bg} border ${s.border} rounded-xl p-5 transition
                     hover:shadow-md hover:scale-[1.01] cursor-pointer
                     focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-brand`}>
      <div className="flex items-center justify-between text-xs uppercase text-slate-700 font-semibold">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
          {label}
        </div>
        <span className="text-slate-400 text-lg leading-none">›</span>
      </div>
      <div className={`text-3xl font-bold mt-1 ${s.value}`}>{count}</div>
      <div className="text-xs text-slate-600 mt-1">{hint}</div>
    </Link>
  );
}

function UrgencySection({ title, accent, items, standWerte, onReload }: {
  title: string;
  accent: 'red' | 'orange' | 'yellow';
  items: DashboardItem[];
  standWerte: import('../lib/api').StandWert[];
  onReload: () => void;
}) {
  const s = urgencyStyles[accent];
  return (
    <div className={`${s.bg} border ${s.border} rounded-xl overflow-hidden mb-4`}>
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-white/50">
        <h3 className={`font-semibold ${s.value}`}>{title}</h3>
        <span className="text-xs text-slate-600">{items.length} Einträge</span>
      </div>
      <table className="w-full text-sm bg-white">
        <thead className="text-xs text-slate-600 uppercase bg-slate-50">
          <tr>
            <th className="text-left px-5 py-2 font-medium w-24">Frist</th>
            <th className="text-left px-5 py-2 font-medium">DV-Nr</th>
            <th className="text-left px-5 py-2 font-medium">Kunde</th>
            <th className="text-left px-5 py-2 font-medium">Bezeichnung</th>
            <th className="text-left px-5 py-2 font-medium">SB</th>
            <th className="text-left px-5 py-2 font-medium">Stand</th>
            <th className="text-right px-5 py-2 font-medium">Zeit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map(d => {
            const days = d.days_to_delivery;
            const frist = days < 0 ? `${Math.abs(days)} Tage verspätet`
                        : days === 0 ? 'HEUTE'
                        : days === 1 ? 'morgen'
                        : `in ${days} Tagen`;
            return (
              <tr key={d.id} className="hover:bg-slate-50">
                <td className={`px-5 py-2 font-bold whitespace-nowrap ${s.value}`}>{frist}</td>
                <td className="px-5 py-2">
                  <Link to={`/drueckteile/${d.id}`} className="font-mono text-xs text-brand hover:underline">
                    {d.dv_nummer ?? '—'}
                  </Link>
                </td>
                <td className="px-5 py-2">{d.kunde_name ?? '—'}</td>
                <td className="px-5 py-2">
                  {d.bestellnr}
                  {d.priority === 'eilig'    && <span className="ml-2 text-xs font-bold text-red-600">EILIG</span>}
                  {d.priority === 'kritisch' && <span className="ml-2 text-xs font-bold text-red-800">KRITISCH</span>}
                </td>
                <td className="px-5 py-2">{d.sachbearbeiter_name ?? '—'}</td>
                <td className="px-5 py-2">
                  <StandCell
                    drueckteilId={d.id}
                    currentStandId={d.stand_id}
                    currentStandWert={d.stand_wert}
                    currentStandFarbe={d.stand_farbe}
                    updatedAt={d.updated_at}
                    standWerte={standWerte}
                    onChanged={onReload} />
                </td>
                <td className="px-5 py-2 text-right">{d.gesamtzeit}h</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
